import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { LanguageSwitcherComponent } from '../../core/i18n/language-switcher.component';
import { LocaleService } from '../../core/i18n/locale.service';
import { TranslatePipe } from '../../core/i18n/translate.pipe';
import { AuthService } from '../../core/services/auth.service';
import { MasterCompanyService } from '../../core/services/master-company.service';

@Component({
  selector: 'app-auth',
  standalone: true,
  imports: [FormsModule, TranslatePipe, LanguageSwitcherComponent],
  templateUrl: './auth.html',
  styleUrl: './auth.css',
})
export class AuthComponent implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly masterCompany = inject(MasterCompanyService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly locale = inject(LocaleService);

  userName = '';
  password = '';
  readonly passwordVisible = signal(false);
  readonly configLoading = signal(true);
  private readonly configErrorSource = signal<unknown>(null);
  readonly submitting = signal(false);
  private readonly formErrorSource = signal<
    { kind: 'i18n'; key: string } | { kind: 'http'; err: unknown } | null
  >(null);

  readonly configError = computed(() => {
    this.locale.locale();
    const err = this.configErrorSource();
    if (!err) return null;
    return AuthService.getErrorMessage(err, this.locale.locale());
  });

  readonly formError = computed(() => {
    this.locale.locale();
    const src = this.formErrorSource();
    if (!src) return null;
    if (src.kind === 'i18n') return this.locale.t(src.key);
    return AuthService.getErrorMessage(src.err, this.locale.locale());
  });

  ngOnInit(): void {
    this.warmMasterCompanyConfig();
  }

  togglePasswordVisible(): void {
    this.passwordVisible.update((v) => !v);
  }

  retryConfig(): void {
    this.masterCompany.clearCache();
    this.configErrorSource.set(null);
    this.configLoading.set(true);
    this.warmMasterCompanyConfig();
  }

  onSubmit(event: Event): void {
    event.preventDefault();
    this.formErrorSource.set(null);

    const u = this.userName.trim();
    if (!u || !this.password) {
      this.formErrorSource.set({ kind: 'i18n', key: 'auth.error.fillCredentials' });
      return;
    }

    this.submitting.set(true);
    this.auth.login(u, this.password).subscribe({
      next: () => {
        this.submitting.set(false);
        const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
        void this.router.navigateByUrl(returnUrl?.startsWith('/') ? returnUrl : '/dashboard');
      },
      error: (err: unknown) => {
        this.submitting.set(false);
        this.formErrorSource.set({ kind: 'http', err });
      },
    });
  }

  private warmMasterCompanyConfig(): void {
    this.masterCompany
      .getMasterCompanyId()
      .then(() => {
        this.configLoading.set(false);
        this.configErrorSource.set(null);
      })
      .catch((err: unknown) => {
        this.configLoading.set(false);
        this.configErrorSource.set(err);
      });
  }
}
